import type { Cycle, Phase } from "../hooks/useCycles";
import { resolveStepProgressStatus } from "./stepProgressStatus";

type CollectionPhase = Pick<Phase, "type" | "startDate" | "endDate">;

/**
 * The sperm-freezing steps before the sample is with the lab, as the treatment
 * hub stores them. Screening has no phase of its own and lives on `protocol`
 * (`STEP_TO_PHASE_TYPE` in `usePhases`).
 */
const SCREENING = { stepId: "screening", phaseType: "protocol" };
const COLLECTION = {
  stepId: "sperm_collection",
  phaseType: "sperm_collection",
};
const STEP_IDS = ["pre_treatment", SCREENING.stepId, COLLECTION.stepId];

function step(
  which: { stepId: string; phaseType: string },
  phases: readonly CollectionPhase[],
  todayIso: string,
) {
  const phase = phases.find((candidate) => candidate.type === which.phaseType);
  return {
    phase,
    ...resolveStepProgressStatus({
      stepId: which.stepId,
      stepIds: STEP_IDS,
      phase,
      todayIso,
    }),
  };
}

export type SpermCollectionFacts = {
  /**
   * Where the collection stands. `booked` is a date today or later; `overdue`
   * is a booked date already behind with the step not marked done, which the
   * patient has to log before Home can follow the sample to the lab.
   */
  collection:
    | { status: "booked"; on: string }
    | { status: "overdue"; on: string }
    | { status: "not-booked" };
  /** Screening tests: done, booked for a date, or not recorded at all. */
  screening:
    | { status: "done" }
    | { status: "booked"; on: string }
    | { status: "not-recorded" };
  clinic: string | null;
};

/**
 * What the collection-day Home can state for certain about a sperm-freezing
 * cycle, read from the steps the treatment hub records and through the hub's
 * own status rule, so the two never disagree.
 */
export function spermCollectionFacts(params: {
  cycle: Pick<Cycle, "provider"> | null | undefined;
  phases: readonly CollectionPhase[];
  todayIso: string;
}): SpermCollectionFacts {
  const { cycle, phases, todayIso } = params;

  const collection = step(COLLECTION, phases, todayIso);
  const collectionOn = collection.phase?.startDate ?? null;
  const collectionFacts: SpermCollectionFacts["collection"] =
    collection.status === "scheduled" && collectionOn
      ? collectionOn >= todayIso
        ? { status: "booked", on: collectionOn }
        : { status: "overdue", on: collectionOn }
      : { status: "not-booked" };

  const screening = step(SCREENING, phases, todayIso);
  const screeningOn = screening.phase?.startDate ?? null;
  const screeningFacts: SpermCollectionFacts["screening"] =
    screening.status === "completed"
      ? { status: "done" }
      : screening.status === "scheduled" && screeningOn
        ? { status: "booked", on: screeningOn }
        : { status: "not-recorded" };

  return {
    collection: collectionFacts,
    screening: screeningFacts,
    clinic: cycle?.provider?.trim() || null,
  };
}
